import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Calendar, ArrowLeft, Play, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppWidget from "../components/WhatsAppWidget";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${id}`);
      setEvent(response.data);
    } catch (error) {
      console.error("Error fetching event:", error);
      navigate("/gallery");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-ZA", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.match(
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    );
    return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : url;
  };

  const openLightbox = (index) => {
    setSelectedImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImageIndex(null);
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    if (event && event.images) {
      setSelectedImageIndex((prev) => 
        prev === 0 ? event.images.length - 1 : prev - 1
      );
    }
  };

  const goToNext = (e) => {
    e.stopPropagation();
    if (event && event.images) {
      setSelectedImageIndex((prev) => 
        prev === event.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === "ArrowLeft") goToPrevious(e);
      if (e.key === "ArrowRight") goToNext(e);
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImageIndex, event]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto"></div>
          <p className="mt-4 text-charcoal/60">Loading event...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={event.title}
        description={event.short_description}
        image={event.cover_image}
        url={`https://mhesheattorneys.co.za/gallery/${event.id}`}
        type="article"
      />
      <Header />

      {/* Hero Image */}
      <section className="pt-20 relative">
        <div className="aspect-[21/9] w-full overflow-hidden">
          <img
            src={event.cover_image}
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/20 to-transparent" />
        </div>

        {/* Back Button */}
        <div className="absolute top-28 left-4 sm:left-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/gallery")}
            className="bg-white/90 hover:bg-white text-charcoal rounded-none"
            data-testid="back-to-gallery-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Gallery
          </Button>
        </div>

        {/* Event Type Badge */}
        <div className="absolute bottom-8 left-4 sm:left-8">
          <span className="bg-brand-gold text-charcoal px-4 py-2 text-sm font-semibold uppercase tracking-wider">
            {event.event_type}
          </span>
        </div>
      </section>

      {/* Event Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Date */}
            <div className="flex items-center gap-2 text-charcoal/50 mb-4">
              <Calendar className="h-5 w-5" />
              <span className="font-sans">{formatDate(event.event_date)}{event.event_end_date ? ` — ${formatDate(event.event_end_date)}` : ''}</span>
            </div>

            {/* Title */}
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-charcoal leading-tight tracking-tighter mb-8">
              {event.title}
            </h1>

            {/* Description */}
            <div className="prose prose-lg max-w-none">
              <p className="text-charcoal/70 font-sans text-lg leading-relaxed whitespace-pre-line">
                {event.full_description}
              </p>
            </div>

            {/* Video Section */}
            {event.video_url && (
              <div className="mt-12">
                <h2 className="font-serif text-2xl text-charcoal mb-6">
                  Event Video
                </h2>
                {showVideo ? (
                  <div className="aspect-video w-full">
                    <iframe
                      src={getYouTubeEmbedUrl(event.video_url)}
                      title={event.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div
                    className="aspect-video w-full relative cursor-pointer group"
                    onClick={() => setShowVideo(true)}
                  >
                    <img
                      src={event.cover_image}
                      alt="Video thumbnail"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-charcoal/50 flex items-center justify-center group-hover:bg-charcoal/60 transition-colors">
                      <div className="w-20 h-20 bg-brand-gold rounded-full flex items-center justify-center">
                        <Play className="h-8 w-8 text-charcoal ml-1" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Image Gallery */}
            {event.images && event.images.length >= 1 && (
              <div className="mt-12">
                <h2 className="font-serif text-2xl text-charcoal mb-6">
                  Event Gallery
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {event.images.map((image, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="aspect-square overflow-hidden cursor-pointer"
                      onClick={() => openLightbox(index)}
                    >
                      <img
                        src={image}
                        alt={`${event.title} - Image ${index + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-light-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl text-charcoal mb-4">
            Interested in Our Services?
          </h2>
          <p className="text-charcoal/60 font-sans mb-8 max-w-xl mx-auto">
            Contact Mheshe Attorneys today to discuss your legal needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/#lead-capture">
              <Button className="btn-gold px-8 py-4 text-base font-semibold uppercase tracking-wider rounded-none">
                Book a Consultation
              </Button>
            </Link>
            <Link to="/gallery">
              <Button
                variant="outline"
                className="btn-outline-gold px-8 py-4 text-base font-semibold uppercase tracking-wider rounded-none"
              >
                View More Events
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal with Navigation */}
      {selectedImageIndex !== null && event.images && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 text-white hover:text-brand-gold transition-colors z-10"
            onClick={closeLightbox}
          >
            <X className="h-8 w-8" />
          </button>

          {/* Previous Button */}
          {event.images.length > 1 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-brand-gold transition-colors z-10 bg-black/50 hover:bg-black/70 p-2 rounded-full"
              onClick={goToPrevious}
            >
              <ChevronLeft className="h-10 w-10" />
            </button>
          )}

          {/* Image */}
          <img
            src={event.images[selectedImageIndex]}
            alt={`${event.title} - Image ${selectedImageIndex + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next Button */}
          {event.images.length > 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-brand-gold transition-colors z-10 bg-black/50 hover:bg-black/70 p-2 rounded-full"
              onClick={goToNext}
            >
              <ChevronRight className="h-10 w-10" />
            </button>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 font-sans text-sm">
            {selectedImageIndex + 1} / {event.images.length}
          </div>
        </div>
      )}

      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default EventDetailPage;
